import { useEffect, useState } from "react";
import { Plus, Minus, Check, Package, Clock } from "lucide-react";
import { getCatalog } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { AdvFormats } from "@/components/campaign/AdvFormats";

export const MAX_QTY = 10;
export const locItem = (it, lang) => (lang === "it" ? it : { ...it, ...(it.i18n?.[lang] || {}) });
export const money = (n) => `CHF ${Number(n).toFixed(2).replace(".00", "")}`;

export const useCatalog = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { getCatalog().then((d) => setItems(d.items)).catch(() => setItems([])); }, []);
  return items;
};

export const Qty = ({ qty, onChange, testid }) => (
  <div data-testid={testid} className="inline-flex items-center border border-[#2B3036] bg-white">
    <button type="button" data-testid={`${testid}-minus`} onClick={() => onChange(qty - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#f4f6f7]"><Minus className="w-3.5 h-3.5" /></button>
    <span data-testid={`${testid}-value`} className="w-8 text-center font-mono text-sm">{qty}</span>
    <button type="button" data-testid={`${testid}-plus`} disabled={qty >= MAX_QTY} onClick={() => onChange(qty + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-[#f4f6f7] disabled:opacity-30"><Plus className="w-3.5 h-3.5" /></button>
  </div>
);

const Card = ({ it, qty, setQty, suggested }) => {
  const { lang, tr } = useLang();
  const l = locItem(it, lang);
  const kit = it.kind === "kit";
  const on = qty > 0;
  return (
    <div data-testid={`catalog-card-${it.id}`} className={`group relative flex flex-col p-5 border transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 ${kit ? "bg-[#2B3036] text-white border-[#2B3036]" : "bg-white"} ${on ? "border-[#E0262C] shadow-[0_8px_30px_-12px_rgba(224,38,44,0.45)]" : kit ? "" : "border-[#cfd6d9] hover:border-[#2B3036]"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={`font-mono text-xs ${kit ? "text-[#E0262C]" : "text-[#a1a1aa]"}`}>{kit ? <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" /> KIT</span> : it.code}</span>
        {suggested && <span data-testid={`catalog-suggested-${it.id}`} className="text-[9px] uppercase tracking-widest font-mono px-2 py-0.5 bg-[#E0262C] text-white">{tr("Consigliato", "Recommended")}</span>}
      </div>
      <h4 className="font-display text-lg leading-tight">{l.name}</h4>
      <p className={`text-xs leading-relaxed mt-2 flex-1 ${kit ? "text-[#c4c9ce]" : "text-[#5b6168]"}`}>{l.description}</p>
      {it.id === "adv_site" && <AdvFormats dark={kit} />}
      {kit && l.includes?.length > 0 && (
        <ul className="mt-3 space-y-1">{l.includes.map((x) => <li key={x} className="text-[11px] flex gap-1.5 text-[#d4d4d8]"><Check className="w-3 h-3 text-[#10b981] mt-0.5" />{x}</li>)}</ul>
      )}
      <div className={`mt-4 pt-4 border-t flex items-center justify-between gap-3 ${kit ? "border-[#3f454c]" : "border-[#e4e4e7]"}`}>
        <div>
          <div data-testid={`catalog-price-${it.id}`} className="font-display text-2xl" dir="ltr">+{money(it.price)}</div>
          <div className={`text-[10px] font-mono inline-flex items-center gap-1 ${kit ? "text-[#a1a1aa]" : "text-[#71717a]"}`}><Clock className="w-3 h-3" /> {it.days} {tr("giorni", "days")}</div>
        </div>
        {on ? <Qty qty={qty} onChange={setQty} testid={`catalog-qty-${it.id}`} /> : (
          <button type="button" data-testid={`catalog-add-${it.id}`} onClick={() => setQty(1)}
            className={`h-10 px-4 text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${kit ? "bg-white text-[#2B3036] hover:bg-[#E0262C] hover:text-white" : "bg-[#2B3036] text-white hover:bg-[#E0262C]"}`}>
            <Plus className="w-4 h-4" /> {tr("Aggiungi", "Add")}
          </button>
        )}
      </div>
    </div>
  );
};

export const ServiceCatalog = ({ items, cart, setQty, suggested = [] }) => {
  const { tr } = useLang();
  if (!items) return <div className="h-40 bg-white border border-[#cfd6d9] animate-pulse max-w-5xl" />;
  const kits = items.filter((i) => i.kind === "kit");
  const services = items.filter((i) => i.kind === "service");
  const grid = (list) => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
      {list.map((it) => <Card key={it.id} it={it} qty={cart[it.id] || 0} setQty={(q) => setQty(it.id, q)} suggested={suggested.includes(it.id)} />)}
    </div>
  );
  return (
    <div data-testid="service-catalog" className="max-w-5xl space-y-8">
      {kits.length > 0 && <div><div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-3">{tr("Kit già pronti", "Ready-made kits")}</div>{grid(kits)}</div>}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <span className="text-[10px] uppercase tracking-widest font-mono text-[#71717a]">{tr("Servizi singoli · durata 30 giorni · cumulabili", "Single services · 30-day duration · stackable")}</span>
          <span className="text-[11px] text-[#5b6168]">{tr("Scegli lo stesso servizio più volte nello stesso mese per raddoppiare le prestazioni.", "Pick the same service several times in the same month to double the output.")}</span>
        </div>
        {grid(services)}
      </div>
    </div>
  );
};
